import React from 'react'
import { Link } from 'react-router-dom'
import { FaFish } from 'react-icons/fa'

function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple Navbar */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <FaFish className="h-8 w-8 text-blue-600" />
              <span className="font-bold text-xl text-gray-900">AnglersAI</span>
            </Link>
          </div>
        </div>
      </nav>
      
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <Link to="/" className="text-blue-600 hover:text-blue-800 mb-8 inline-block">
          ← Back to Home
        </Link>
        
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-6">
          <section>
            <p className="text-gray-600 mb-6">
              Last updated: {new Date().toLocaleDateString()}
            </p>
            
            <p className="text-gray-700 mb-6">
              These Terms of Service ("Terms") govern your use of the AnglersAI mobile application (the "Service") 
              operated by Wright Media, LLC ("we," "us," or "our").
            </p>
            
            <p className="text-gray-700 mb-6">
              By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any 
              part of these terms, then you may not access the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By downloading, installing, or using AnglersAI, you acknowledge that you have read, understood, 
              and agree to be bound by these Terms of Service and our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">
              AnglersAI provides fish measurement and weight estimation services using artificial intelligence 
              and augmented reality technology. The Service includes:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Fish length measurement using camera and AR technology</li>
              <li>Weight estimation based on species-specific algorithms</li>
              <li>Fish species identification</li>
              <li>Photo capture and storage</li>
              <li>Data synchronization across devices (premium feature)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
            <p className="text-gray-700 mb-4">
              When you create an account with us, you must provide information that is accurate, complete, 
              and current at all times. You are responsible for safeguarding your account and for all activities 
              that occur under your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Subscriptions and Payments</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Subscription Plans</h3>
            <p className="text-gray-700 mb-4">
              AnglersAI offers the following subscription plans:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Monthly Subscription: $9.99 per month</li>
              <li>Annual Subscription: $49.99 per year</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Payment Terms</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Payment will be charged to your iTunes Account at confirmation of purchase</li>
              <li>Subscription automatically renews unless auto-renew is turned off at least 24-hours before the end of the current period</li>
              <li>Account will be charged for renewal within 24-hours prior to the end of the current period</li>
              <li>Subscriptions may be managed and auto-renewal may be turned off by going to your Account Settings after purchase</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Free Trial</h3>
            <p className="text-gray-700 mb-4">
              If offered, any unused portion of a free trial period will be forfeited when you purchase a subscription.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Acceptable Use</h2>
            <p className="text-gray-700 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Use the Service for any unlawful purpose or in violation of any laws</li>
              <li>Attempt to reverse engineer, decompile, or hack the Service</li>
              <li>Use the Service to harass, abuse, or harm another person</li>
              <li>Upload or transmit viruses or any other type of malicious code</li>
              <li>Use the Service for commercial purposes without our permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Accuracy of Measurements</h2>
            <p className="text-gray-700 mb-4">
              While we strive to provide accurate measurements and weight estimates, AnglersAI is intended for 
              recreational and informational purposes only. We do not guarantee the accuracy of measurements or 
              weight calculations. For official tournament or regulatory purposes, please use certified measuring 
              devices.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              The Service and its original content, features, and functionality are and will remain the exclusive 
              property of Wright Media, LLC and its licensors. The Service is protected by copyright, trademark, 
              and other laws. Our trademarks may not be used in connection with any product or service without 
              our prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. User Content</h2>
            <p className="text-gray-700 mb-4">
              By uploading photos or other content to AnglersAI, you grant us a non-exclusive, worldwide, 
              royalty-free license to use, store, and display your content for the purpose of providing and 
              improving our Service. You retain all rights to your content.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Privacy</h2>
            <p className="text-gray-700 mb-4">
              Your use of our Service is also governed by our Privacy Policy. Please review our Privacy Policy, 
              which also governs the Site and informs users of our data collection practices.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Disclaimers</h2>
            <p className="text-gray-700 mb-4">
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. WE MAKE NO WARRANTIES, EXPRESSED 
              OR IMPLIED, AND HEREBY DISCLAIM ALL OTHER WARRANTIES INCLUDING, WITHOUT LIMITATION, IMPLIED WARRANTIES 
              OR CONDITIONS OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT OF 
              INTELLECTUAL PROPERTY OR OTHER VIOLATION OF RIGHTS.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              IN NO EVENT SHALL WRIGHT MEDIA, LLC, NOR ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, 
              OR AFFILIATES, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
              INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, 
              RESULTING FROM YOUR USE OF THE SERVICE.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Termination</h2>
            <p className="text-gray-700 mb-4">
              We may terminate or suspend your account immediately, without prior notice or liability, for any 
              reason whatsoever, including without limitation if you breach the Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a 
              revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              These Terms shall be governed and construed in accordance with the laws of the United States, 
              without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about these Terms, please contact us at:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Email: support@wrightmedia.com</li>
              <li>Website: anglersai.com</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  )
}

export default TermsOfService